<template>
  <div class="custom-selector" :class="{ 'is-open': isOpen, 'is-disabled': disabled }">
    <!-- Selector Button -->
    <button
      ref="selectorButton"
      type="button"
      class="selector-button"
      :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
      :disabled="disabled"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      :aria-label="ariaLabel"
      @click="handleToggle"
      @blur="handleBlur"
    >
      <span class="selector-text">
        {{ selectedLabel || placeholder }}
      </span>
      <span class="selector-arrow" :class="{ 'is-open': isOpen }">
        <ChevronDownIcon />
      </span>
    </button>

    <!-- Dropdown Menu -->
    <Transition name="dropdown">
      <div
        v-show="isOpen"
        ref="dropdownMenu"
        class="dropdown-menu"
        :class="[`dropdown-position-${position}`, { 'dropdown-above': position === 'above' }]"
        :style="dropdownPosition"
        role="listbox"
        :aria-label="ariaLabel"
      >
        <div class="dropdown-content">
          <div
            v-for="option in options"
            :key="option.value"
            class="dropdown-option"
            :class="{
              'is-selected': isOptionSelected(option),
              'is-disabled': option.disabled,
            }"
            :data-value="option.value"
            role="option"
            :aria-selected="isOptionSelected(option)"
            :aria-disabled="option.disabled"
            @click="handleOptionSelect(option)"
          >
            <span class="option-label">{{ option.label }}</span>
            <span v-if="isOptionSelected(option)" class="option-checkmark">
              <CheckIcon />
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ChevronDownIcon from './icons/ChevronDownIcon.vue';
import CheckIcon from './icons/CheckIcon.vue';
import { debounce } from '@/vendor/pleased-sdk/util';

interface SelectorOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  components: {
    ChevronDownIcon,
    CheckIcon,
  },
})
export default class SingleSelect extends Vue {
  @Prop({ type: Array, required: true }) readonly options!: SelectorOption[];
  @Prop({ type: [String, Number], default: '' }) readonly value!: string | number;
  @Prop({ type: String, default: 'Select an option' }) readonly placeholder!: string;
  @Prop({ type: String, default: 'Select' }) readonly ariaLabel!: string;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: String, default: 'above' }) readonly position!: 'above' | 'below';

  isOpen = false;
  selectedOption: SelectorOption | null = null;
  dropdownPosition = {
    top: '0px',
    left: '0px',
  };
  private debouncedUpdatePosition = debounce(this.updateDropdownPosition, 300);

  get selectedLabel(): string {
    if (this.selectedOption) {
      return this.selectedOption.label;
    }
    return '';
  }

  mounted() {
    this.updateDropdownPosition();
  }

  @Watch('value', { immediate: true })
  onValueChange(newValue: string | number) {
    this.selectedOption = this.options.find((option) => option.value === newValue) || null;
  }

  @Watch('options', { immediate: true })
  onOptionsChange() {
    if (this.value) {
      this.selectedOption = this.options.find((option) => option.value === this.value) || null;
    }
  }

  updateDropdownPosition() {
    const button = this.$refs.selectorButton as HTMLElement;
    const dropdown = this.$refs.dropdownMenu as HTMLElement;

    if (!button || !dropdown) return;

    const buttonRect = button.getBoundingClientRect();
    const gap = 10; // 10px gap between selector and dropdown

    // Get the actual dropdown height after it's rendered
    const dropdownHeight = dropdown.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top: number;
    let left = buttonRect.left;

    // Determine if dropdown should go above or below based on available space
    const shouldGoAbove =
      this.position === 'above' ||
      (this.position === 'below' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow);

    if (shouldGoAbove) {
      // Position above the button
      top = buttonRect.top - dropdownHeight - gap;
    } else {
      // Position below the button
      top = buttonRect.bottom + gap;
    }

    // Update dropdown position
    this.dropdownPosition = {
      top: `${top}px`,
      left: `${left}px`,
    };
  }

  handleToggle() {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
    }
  }

  handleBlur(event: FocusEvent) {
    // Use a small delay to allow option clicks to register
    setTimeout(() => {
      if (!this.$el.contains(event.relatedTarget as Node)) {
        this.isOpen = false;
      }
    }, 100);
  }

  handleOptionSelect(option: SelectorOption) {
    if (option.disabled) return;

    this.selectedOption = option;
    this.isOpen = false;
    this.$emit('input', option.value);
    this.$emit('change', option.value);

    // Focus back to the button for accessibility
    (this.$refs.selectorButton as HTMLElement)?.focus();
  }

  isOptionSelected(option: SelectorOption) {
    return option.value === this.value;
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';

.custom-selector {
  position: relative;
  display: inline-block;
  width: 100%;

  &.is-open {
    z-index: 1000;
  }

  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.selector-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem 0.75rem;
  min-height: 28px;
  background-color: $tr-controls-selector-background;
  border: 1px solid $tr-controls-selector-border-color;
  border-radius: 4px;
  color: $tr-controls-text-color;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(.is-disabled) {
    border-color: darken($tr-controls-selector-border-color, 10%);
    background-color: darken($tr-controls-selector-background, 5%);
  }

  &:focus {
    outline: none;
    border-color: darken($tr-controls-selector-border-color, 20%);
    box-shadow: 0 0 0 2px rgba($tr-controls-selector-border-color, 0.2);
  }

  &.is-open {
    border-color: darken($tr-controls-selector-border-color, 20%);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.selector-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  transition: transform 0.2s ease;
  color: $tr-controls-text-color;

  &.is-open {
    transform: rotate(180deg);
  }

  svg {
    width: 12px;
    height: 12px;
  }
}

.dropdown-menu {
  position: fixed;
  background-color: $tr-controls-selector-background;
  border: 1px solid $tr-controls-selector-border-color;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow-y: auto;
  min-width: 50px;
  max-width: 150px;
  width: auto;
  max-height: 200px; // Maximum height with scroll
  transform: translateY(0);

  &.dropdown-position-above {
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  }

  &.dropdown-position-below {
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.dropdown-content {
  padding: 0.25rem 0;
}

.dropdown-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  min-height: 28px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(.is-disabled) {
    background-color: darken($tr-controls-selector-background, 5%);
  }

  &.is-focused {
    background-color: darken($tr-controls-selector-background, 10%);
  }

  &.is-selected {
    background-color: rgba($tr-controls-selector-border-color, 0.1);
    font-weight: 600;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
    color: lighten($tr-controls-text-color, 30%);
  }
}

.option-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.option-checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.5rem;
  color: $tr-controls-text-color;

  svg {
    width: 16px;
    height: 16px;
  }
}

// Transition animations
.dropdown-enter-active,
.dropdown-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}

// Scrollbar styling
.dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background-color: rgba($tr-controls-selector-border-color, 0.3);
  border-radius: 3px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background-color: rgba($tr-controls-selector-border-color, 0.5);
}
</style>
